/**
 * @module geo
 */

import objectPath from 'object-path';
import {Store} from 'redux';
import watch from 'redux-watch';
import {Observable} from 'rxjs/Observable';
import {fromPromise} from 'rxjs/observable/fromPromise';
import filter from 'lodash.filter';

import {addMarkersCreatorP, gotoExtentCreatorP, gotoInitExtentCreatorP, gotoMarkerCreatorP, gotoPointCreatorP, listenMapEventsCreatorP, panToCreatorP, removeMarkersCreatorP, setEntriesVisibilityCreatorP, zoomInCreatorP, zoomOutCreatorP, zoomToScaleCreatorP} from './actions/creators';

import {Watch} from './decorators/watch';
import {MapEntryDeserializer} from './Deserializer';
import {Extent} from './extent';
import {HashMapEntry, HashMapEntryVisibility, MapEntry} from './map/layer/map.entry';
import {GO_TO_POINT} from './payload/facade/GO_TO_POINT';

import {EntryVisibilityPayload} from './payload/facade/SET_ENTRIES_VISIBILITIES';
import {GetImage} from './types/services/get.image';
import {AppStore} from './store/app';
import {stateOMap, stateOMapEntries} from './store/selectors';
import {ExtentOptions} from './types/extent';
import {MapStore, MapOptions} from './types/map';
import {IHashEntry} from './types/map/layer/map.entry';
import {Marker} from './types/map/marker';
import {Watcher} from './watcher';
import { Success, Failure } from 'typescript-fsa';
import { ADD_MARKERS } from './payload/facade';
import { AddMarkersDone } from './payload/response';
import { Application } from './application';
import { ServiceGetImageMapParameters } from './payload/facade/services/SERVICE_GET_IMAGE_MAP';

/**
 * The Map class handle all properties which can bet set, listened .
 * This is also the main point exposing convenient methods for launching actions
 *
 *
 * Map properties are of the type of [[MapOptions]]
 *
 */
export interface Map extends Watcher {}

@Watch({})

export class Map extends MapStore {

  /**
   * @private
   */
  public application?:Application;
  /**
   * observers bound to this map
   * @private
   */
  public observers: any;
  /**
   * default path for listening properties
   * @type {string}
   * @private
   */
  public path: string = 'map';
  /**
   * store bound to this map
   */
  public store: Store<AppStore>|null = null;
  /**
   * default listeners for map object
   */
  public listenersNeeded:any = {'pointerClick': 'POINTER_CLICK', 'pointerMove': 'POINTER_MOVE'};
  /**
   * Maps entries referenced
   */
  public entries: HashMapEntry = {};


  /**
   * Hash of map entries referenced, by key
   */
  protected entriesByKey: HashMapEntry = {};
  /**
   * map crs
   */
  public crs?: string;

  /**
   * Create a Map object with all map options
   * @private
   * @param {MapOptions} options
   */
  constructor(options: MapOptions) {
    super();
    Object.assign(this, options);
    this.observers = {};
  }

/**
 * 
 * @param Application 
 * @private
 */
  public setApplication( application:Application){
    this.application=application;
  }

  /**
   * connect map to store
   * @private
   */
  public connect(store: Store<AppStore>) {
    const w = watch(() => stateOMap(store.getState()));
    store.subscribe(w((newVal: MapOptions, oldVal: MapOptions) => {
      const lastEntries = this.entries;

      Object.assign(this, newVal);
      this.entries = lastEntries;
    }));
    this.store = store;
    const we = watch(() => stateOMapEntries(store.getState()));
    store.subscribe(we((newVal: IHashEntry, oldVal: IHashEntry) => {
      const newEntries: HashMapEntry = {};
      for (const entryKey of Object.keys(newVal)) {
        const entry = newVal[entryKey];
        if (this.store == null) {
          throw new Error('store is null');
        }
        const entryObj: MapEntry =
            MapEntryDeserializer.build(entry, this.store);

        if (entry.entries !== undefined && entry.entries !== null &&
            entry.entries) {
          entryObj.entries =
              this.recurseBuildEntry(entry.entries, entryObj.key);
        }

        newEntries[entryObj.key] = entryObj;
        if (this.entries !== null && this.entries[entryObj.key] !== undefined &&
            this.entries[entryObj.key] != null) {
          if (newEntries != null && newEntries[entryObj.key] != null) {
            newEntries[entryObj.key] = Object.assign(
                this.entries[entryObj.key], newEntries[entryObj.key]);
          }
        }
        if (this.entriesByKey[entryObj.key] === undefined) {
          this.entriesByKey[entryObj.key] = newEntries[entryObj.key];
        } else {
          Object.assign(
              this.entriesByKey[entryObj.key] = newEntries[entryObj.key]);
        }
      }
      this.entries = newEntries;
    }));
  }

  /**
   * Get map entry by key
   */
  public getEntryById(id: string): MapEntry {
    if (this.entriesByKey && this.entriesByKey[id] !== null) {
      return this.entriesByKey[id];
    }
    throw new Error('unknown entry id ' + id);
  }

  /**
   * Get all map entries matching the given name
   * @param name
   */
  public getEntryByName(name: string): MapEntry[] {
    return this.getEntryBy({'name': name});
  }

  /**
   * Get all entries matching the specify propertie
   * @param filterO object of type : {propertyName:value}
   */
  public getEntryBy(filterO: object): MapEntry[] {
    const values = Object.values(this.entriesByKey);


    const result: MapEntry[] = filter(values, filterO);

    return result;
  }

  /**
   * Define entries visibilities
   * @param ent entries to update visibility
   */
  public setEntriesVisibility(ent: HashMapEntryVisibility): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    const ke: EntryVisibilityPayload[] = [];
    for (const keyIdx of Object.keys(ent)) {
      const visibility = ent[keyIdx];
      ke.push({

        key: keyIdx,
        visible: visibility
      });
    }
    return fromPromise(
        setEntriesVisibilityCreatorP({entries: ke}, this.store.dispatch));
  }

  /**
   * Active listen of map events when call of geoapp.map.on(eventname,..) and
   * eventname is in listenerNeeded.
   * @private
   * @param events
   */
  public activateEvents(events: string[]): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(listenMapEventsCreatorP({events}, this.store.dispatch));
  }

  /**
   * Active listen of map events.
   * @param events
   */
  public activateMapEvents(events: string[]): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(listenMapEventsCreatorP({events}, this.store.dispatch));
  }

  /**
   * Add  markers on map
   *
   *
   * ```javascript
   *   var marker = {
   *                position: {
   *                  coordinates: [1,1],
   *                  crs: 'EPSG:4326'
   *             },
   *              id: 1,
   *              tooltip: null,
   *               imageUrl:
   * 'https://geoapi.business-geografic.com/docs/img/marker.png', size: { w: 20,
   *                  h: 34
   *              }
   *           };
   *   geoMap.addMarkers([marker]);
   *
   *
   * ```
   *
   *
   * @param markers list of marker objects
   */
  public addMarkers(markers: Marker[]): Observable<Success<ADD_MARKERS,AddMarkersDone>>{
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(addMarkersCreatorP({markers}, this.store.dispatch));
  }

  /**
   * center on coordinate
   * @param pointToCenter
   */
  public centerOn(pointToCenter: GO_TO_POINT): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(gotoPointCreatorP(pointToCenter, this.store.dispatch));
  }

  /**
   * Zoom map to specify scale denominator (to go to 1:25000 pass value 25000)
   * @param scaleDenominator
   */
  public zoomTo(scaleDenominator: number): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(
        zoomToScaleCreatorP({scaleDenominator}, this.store.dispatch));
  }

  /**
   * Zoom in on the current view
   */
  public zoomIn(): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(zoomInCreatorP({}, this.store.dispatch));
  }

  /**
   * Zoom out of the current view
   */
  public zoomOut(): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(zoomOutCreatorP({}, this.store.dispatch));
  }

  /**
   * Pan map to left, right, up or down
   * @param pan direction of pan, one of left, right, up or down
   */
  public panTo(pan: string): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(panToCreatorP({direction: pan}, this.store.dispatch));
  }

  /**
   * Go back to initial extent of themap
   */
  public gotoInitExtent(): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(gotoInitExtentCreatorP({}, this.store.dispatch));
  }

  /**
   * Set the new extent for the map
   * @deprecated, see setExtent
   */
  public gotoExtent(extent: ExtentOptions|number[], crs?: any):
      Observable<any> {
        return this.setExtent(extent, crs);
  }

  /**
   * Set the new extent for the map
   */
  public setExtent(extent: ExtentOptions|number[], crs?: any):
      Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    if (Array.isArray(extent)) {
      extent = new Extent(extent, crs);
    }
    return fromPromise(
        gotoExtentCreatorP({'extent': extent}, this.store.dispatch));
  }
  /**
   * Remove the specified markers from the map
   * @param  markers list of ids of the markers to remove
   */
  public removeMarkers(markers: string[]): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(removeMarkersCreatorP({markers}, this.store.dispatch));
  }

  /**
   * Center map on a marker
   * @param id marker id
   * @param buffer buffer in meter to view around the marker
   */
  public centerOnMarker(id: string, buffer: number): Observable<any> {
    if (this.store == null) {
      throw new Error('store is null');
    }
    return fromPromise(gotoMarkerCreatorP({id, buffer}, this.store.dispatch));
  }

  /**
   * Export map as image and return a subscription
   *
   *
   * for instance :
   *
   *
   * ```javascript
   * window.geoApp.map.serviceExportAsImage({}).subscribe(
   *      resultset=>{
   *          console.debug(resultset.result);
   *          console.debug('exported map')
   *      }
   *  )
   *
   * ```
   *
   * @param parameters print parameters
   */
  
  public serviceExportAsImage(parameters: ServiceGetImageMapParameters):
      Observable<GetImage> {
        if(this.application){
          return this.application.Services.GetImageMap.getDefaultImageMap(parameters);
        }else{
          throw new Error('map has been not initialized');
        }
  }

  /**
   * build entry tree and get all entries by key to construct/update
   * entriesByKey variable
   * @private
   * @param newVal
   * @param precedKey
   */
  protected recurseBuildEntry(newVal: any, precedKey: string): HashMapEntry {
    precedKey = precedKey + '.entries.';
    const newEntries: HashMapEntry = {};
    if (this.store == null) {
      throw new Error('store is null');
    }

    for (const entryKey of Object.keys(newVal)) {
      const entry = newVal[entryKey];
      let entryObj: MapEntry = MapEntryDeserializer.build(entry, this.store);
      entryObj.fullPath = precedKey + entryObj.key;
      if (entry.entries !== undefined && entry.entries !== null &&
          entry.entries) {
        entryObj.entries =
            this.recurseBuildEntry(entry.entries, entryObj.fullPath);
      }
      const pathO = entryObj.fullPath.split('.');
      const old = objectPath.get(this.entries, entryObj.fullPath);
      if (old !== undefined && old) {
        entryObj = Object.assign(old, entryObj);
      }
      newEntries[entryObj.key] = entryObj;
      if (newEntries !== undefined && newEntries[entryObj.key] !== undefined) {
        if (this.entriesByKey[entryObj.key] === undefined) {
          this.entriesByKey[entryObj.key] = newEntries[entryObj.key];
        } else {
          Object.assign(
              this.entriesByKey[entryObj.key] = newEntries[entryObj.key]);
        }
      }
    }
    return newEntries;
  }
}
